import type { SsrAppApiDirectOptions } from '@/data/appApi';
import { hasClassicAuth0SessionCookie } from '@/server/utils/hasClassicAuth0SessionCookie';
import { User } from '@/store/user/types';
import apiRequest from '@/utils/apiRequest';
import { RequestUserData } from '@/utils/apiRequest/types';
import { calmLogger } from '@/utils/calmLogger';

export async function getUser(
	userData: RequestUserData,
	opts?: SsrAppApiDirectOptions,
): Promise<User | null> {
	// A full-page OneID login that returns straight to a page leaves only the Auth0 session,
	// never the legacy token, and both transports below authenticate /v2/me from that session.
	if (!userData.userToken && !hasClassicAuth0SessionCookie(userData.cookies)) return null;
	try {
		if (typeof window === 'undefined' && opts?.ctx) {
			// SSR app-api-direct: logged-in callers reach `/v2/me` straight
			// through internal app-api. `requiresAuth: true` matches the authproxy, which
			// injects the Auth0 Bearer for this endpoint. See getFeatureFlags.ts for the
			// lazy-import rationale that keeps `@/data/appApi` out of the client bundle.
			const { appApiServerRequest } = await import('@/data/appApi');
			const { data } = await appApiServerRequest<User>({
				endpoint: 'v2/me',
				ctx: opts.ctx,
				xLanguage: opts.xLanguage,
				requiresAuth: true,
			});
			return data || null;
		}

		const result = await apiRequest({
			endpoint: 'v2/me',
			method: 'GET',
			userData,
		});

		return result?.data || null;
	} catch (err) {
		calmLogger.error('Error retrieving user via GET /me', {}, err);
		return null;
	}
}
